import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { onboardingState, type WeddingSummary } from '@guabi/shared';

/**
 * The getting-started list, shown until it is finished and then never again.
 *
 * Derived from the data rather than stored, so it cannot claim something that is
 * no longer true — delete the last venue and that step reopens. That is also why
 * there is no dismiss button for the list as a whole: a derived list disappears
 * by being completed, which is the only honest way for one to go away.
 *
 * **Finished steps are hidden, not struck through.** Nobody ticks these — a step
 * completes itself when the thing exists, so you never see the moment it crosses
 * out. You only ever come back later to four dead lines above the two that still
 * matter. The count in the header is where progress belongs; "show all" is there
 * because the one honest reason to look at a finished step is to check it really
 * noticed.
 */
export function Onboarding({
  summary,
  hasDate,
  hasPartner,
}: {
  summary: WeddingSummary;
  hasDate: boolean;
  hasPartner: boolean;
}) {
  const { t } = useTranslation();
  const [showAll, setShowAll] = useState(false);
  const state = onboardingState({ hasDate, hasPartner, summary });
  if (state.complete) return null;

  const shown = showAll ? state.steps : state.steps.filter((step) => !step.done);

  return (
    <section className="panel flex flex-col gap-3 p-5">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-section">{t('onboard.title')}</h2>
        <span className="flex items-baseline gap-3">
          <span className="tabular text-caption text-leaf-soft">
            {t('onboard.progress', { done: state.done, total: state.total })}
          </span>
          {state.done > 0 && (
            <button
              type="button"
              onClick={() => setShowAll((v) => !v)}
              className="min-h-tap text-caption text-leaf-soft underline underline-offset-4 hover:text-ink"
            >
              {showAll ? t('onboard.hideDone') : t('onboard.showAll')}
            </button>
          )}
        </span>
      </div>

      <ul className="flex flex-col divide-y divide-line">
        {shown.map((step) => (
          <li key={step.key} className="flex items-center gap-3 py-2">
            {/* Not a checkbox: there is nothing to tick. The step is done when
                the thing exists, and offering a tick would promise otherwise. */}
            <span
              aria-hidden="true"
              className={`inline-flex size-5 shrink-0 items-center justify-center rounded-pill border text-caption ${
                step.done ? 'border-leaf bg-leaf text-cream' : 'border-line text-transparent'
              }`}
            >
              {'✓'}
            </span>
            <span className={`flex-1 text-body ${step.done ? 'text-leaf-soft line-through' : ''}`}>
              {t(`onboard.steps.${step.key}`)}
            </span>
            {!step.done && (
              <Link
                to={`/app/w/${summary.wedId}/${step.tab}`}
                className="min-h-tap shrink-0 text-caption text-brass-text underline underline-offset-4"
              >
                {t('onboard.go')}
              </Link>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
